import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import InterestFreeOption from './InterestFreeOption'
import {
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
  getKlarnaBpMaxLimit,
  isKlarnaBpEnabled,
  isWithinKlarnaBpAvailabilityRange,
} from 'lib/payment/paymentUtils'
import {
  KLARNA_PAYMENT_TYPE,
  KLARNA_BP_PAYMENT_TYPE,
  HOOLAH_BP_PAYMENT_TYPE,
  ATOME_BP_PAYMENT_TYPE,
  AFTER_PAY_BP_PAYMENT_TYPE,
  WAAVE_BP_PAYMENT_TYPE,
} from 'constants/payment'
import { connect } from 'react-redux'
import { getAfterPayBpMaxLimit, isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import { getHoolahBpMaxLimit, isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { getAtomeBpMaxLimit, isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import { formatRoundedAmount, formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GeoContext from 'contexts/geoContext'

const HowToUseSection = styled.div`
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  max-width: ${rem(680)};
`

const PaymentOptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface Props {
  isOpen?: boolean
  onClose?: () => void
  currency: string
  offer?: App.Offer
}

function InterestFreeLearnMoreModal(props: Props) {
  const { isOpen, onClose, currency, offer } = props
  const intl = useIntl()

  const { currentRegionCode } = useContext(GeoContext)

  const payOver4Label = intl.formatMessage({
    id: 'K5WRrOlpnf',
    defaultMessage: 'Pay over 4 interest-free instalments',
    description: 'Title for interest-free payment option with 4 instalments',
  })

  const payOver3Label = intl.formatMessage({
    id: 'Hnxyn+dnKi',
    defaultMessage: 'Pay over 3 interest-free instalments',
    description: 'Title for interest-free payment option with 3 instalments (GBP)',
  })

  const payInThreeLabel = intl.formatMessage({
    id: 'cyhjAsqEuL',
    defaultMessage: 'Pay in three instalments',
    description: 'Title for buy-now-pay-later option with three instalments',
  })

  const payViaDirectDebitLabel = intl.formatMessage({
    id: 'ow3S9/4eQz',
    defaultMessage: 'Pay securely via direct debit',
    description: 'Title for direct debit payment option',
  })

  const noMinimumSpendLabel = intl.formatMessage({
    id: 'lF4yVVrmbU',
    defaultMessage: 'No minimum spend.',
    description: 'Content description for payment option with no minimum spend requirement',
  })

  const products = [
    {
      id: KLARNA_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt="Klarna Pay" height="40" />,
      brand: 'Klarna',
      title: payOver4Label,
      // TODO: i18n - complex pricing string with hardcoded dollar amount
      content: 'For purchases under $2,500',
      to: '/klarna',
      isAvailable: (currency: string, offer?: App.Offer) => isKlarnaEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaAvailabilityRange(p.price))),
    },
    {
      id: KLARNA_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt="Klarna Pay" height="40" />,
      brand: 'Klarna',
      title: currency === 'GBP' ? payOver3Label : payOver4Label,
      // TODO: i18n - complex pricing string with formatRoundedAmount
      content: `For purchases under ${formatRoundedAmount(getKlarnaBpMaxLimit(currency), currency, currentRegionCode)}`,
      to: '/klarna',
      isAvailable: (currency: string, offer?: App.Offer) => isKlarnaBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaBpAvailabilityRange(p.price, currency))),
    },
    {
      id: AFTER_PAY_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-afterpay')} alt="After Pay" height="40" />,
      brand: 'After pay',
      title: payOver4Label,
      // TODO: i18n - complex pricing string with formatRoundedAmount
      content: `For purchases under ${formatRoundedAmount(getAfterPayBpMaxLimit(currency), currency, currentRegionCode)}`,
      to: '/afterpay',
      isAvailable: (currency: string) => isAfterPayBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAfterPayBpAvailabilityRange(p.price, currency))),
    },
    {
      id: HOOLAH_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt="Shopback" height="40" />,
      brand: 'Shopback',
      title: payInThreeLabel,
      // TODO: i18n - complex pricing string with formatRoundedAmountWithDollarType
      content: `For purchases ${formatRoundedAmountWithDollarType(getHoolahBpMaxLimit(currency), currency, currentRegionCode)} and under`,
      to: '/shopbackpaylater',
      isAvailable: (currency: string) => isHoolahBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinHoolahBpAvailabilityRange(p.price, currency))),
    },
    {
      id: ATOME_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt="Atome" height="40" />,
      brand: 'Atome',
      title: payInThreeLabel,
      // TODO: i18n - complex pricing string with formatRoundedAmountWithDollarType
      content: `For purchases ${formatRoundedAmountWithDollarType(getAtomeBpMaxLimit(currency), currency, currentRegionCode)} and under`,
      to: '/atome',
      isAvailable: (currency: string) => isAtomeBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAtomeBpAvailabilityRange(p.price, currency))),
    },
    {
      id: WAAVE_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-waave')} alt="LuxPay" height="15" />,
      brand: 'LuxPay, powered by Waave',
      title: payViaDirectDebitLabel,
      content: noMinimumSpendLabel,
      to: '/luxpay',
      isAvailable: (currency: string) => isWaaveBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinWaaveBpAvailabilityRange(p.price, currency))),
    },
  ]

  const modalTitle = intl.formatMessage({
    id: 'IJSEEbFLhF',
    defaultMessage: 'Interest free payment options',
    description: 'Title for the interest-free payment options modal',
  })

  const modalSubtitle = intl.formatMessage({
    id: 'hTsYeNd34R',
    defaultMessage: 'Select from the following interest free payment options in checkout.',
    description: 'Subtitle explaining user can select from available interest-free payment options',
  })

  const howToUseHeading = intl.formatMessage({
    id: '5k4zvpvWCp',
    defaultMessage: 'How to use this service',
    description: 'Heading for instructions on using interest-free payment service',
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      subtitle={modalSubtitle}
    >
      <VerticalSpacer gap={20}>
        <PaymentOptionSection>
          {products.map(product => {
            if (product.isAvailable(currency, offer)) {
              return (<InterestFreeOption
                key={product.id}
                product={product}
              />)
            }
            return null
          })}
        </PaymentOptionSection>
        <HowToUseSection>
          <Heading variant="heading6">{howToUseHeading}</Heading>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'nBo+6gDoaU',
              defaultMessage: 'Select your Escape and checkout as normal. In the checkout select the interest free payment option and continue from there.',
              description: 'Instructions on how to use interest free payment option',
            })}
          </BodyText>
        </HowToUseSection>
      </VerticalSpacer>
    </Modal>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currency: state.geo.currentCurrency,
  }
}

export default connect(mapStateToProps)(InterestFreeLearnMoreModal)
